import { type FormikProps, type FormikErrors, type FormikTouched } from 'formik'
import { get } from 'lodash-es'
import { useEffect, useState } from 'react'
// TODO: react-text-mask is deprecated — migrate to CurrencyField (src/core/currency/ui/CurrencyField.tsx)
import MaskedInput from 'react-text-mask'

import { DatePicker, Select, TextField as LegacyMirandaTextField } from '@loadsmart/loadsmart-ui'
import type { GenericOption } from '@loadsmart/loadsmart-ui/dist/components/Select/Select.types'
import type { Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import {
  Field,
  Switch,
  type SwitchChangeEventDetail,
  TextArea,
  TextField,
  Layout,
  ToggleGroup,
} from '@loadsmart/miranda-react'

import { useConfigContext } from 'config/ui/ConfigContext'

import {
  type OpenDockWarehouseFormFieldValue,
  type OpenDockWarehouseCustomFormField,
  type OpenDockWarehouseFormField,
  type OpenDockWarehouseFormRefNumbers,
} from '../../infra/ShipmentMapper'
import { DocAndMultiDocField } from './DocAndMultiDocField'

const TWELVE_HOUR_LIMIT = 12

export type OPENDOCK_DYNAMIC_FIELD_TYPE =
  | 'str'
  | 'bigstr'
  | 'timestamp'
  | 'int'
  | 'bool'
  | 'doc'
  | 'multidoc'
  | 'dropdown'
  | 'dropdownmultiselect'
  | 'phone'
  | 'email'
  | 'combobox'
  | 'date'

export type OpenDockField =
  | OpenDockWarehouseCustomFormField
  | OpenDockWarehouseFormField
  | OpenDockWarehouseFormRefNumbers

const TEXT_FIELD_TYPES: Record<string, 'text' | 'number' | 'tel' | 'email'> = {
  str: 'text',
  int: 'number',
  phone: 'tel',
  email: 'email',
}

function buildOptions(options?: string[] | null): GenericOption[] {
  if (!options) {
    return []
  }

  return options.map(option => ({
    label: option,
    value: option,
  }))
}

function getValidationStatus(
  fieldName: string,
  errors: FormikErrors<unknown>,
  touched: FormikTouched<unknown>
) {
  if (get(touched, fieldName) && get(errors, fieldName)) {
    return 'danger'
  }

  return 'default'
}

function ErrorMessage({
  validationStatus,
  errors,
  fieldName,
}: {
  validationStatus: string
  errors: FormikErrors<unknown>
  fieldName: string
}) {
  if (validationStatus === 'danger') {
    return <Field.Hint>{get(errors, fieldName)}</Field.Hint>
  }

  return null
}

function FormField({
  field,
  fieldName,
  children,
  validationStatus,
  errors,
}: {
  field: OpenDockField
  fieldName: string
  children: React.ReactNode
  validationStatus: 'danger' | 'default'
  errors: FormikErrors<unknown>
}) {
  return (
    <Field status={validationStatus} key={fieldName}>
      <Field.Label required={field.required}>{field.label}</Field.Label>
      {children}
      <ErrorMessage validationStatus={validationStatus} errors={errors} fieldName={fieldName} />
    </Field>
  )
}

const AMPM_OPTIONS = [
  { label: 'AM', value: 'AM' },
  { label: 'PM', value: 'PM' },
]

function isTimeValid(time: string) {
  const timeRegex = /^(0[1-9]|1[0-2]):([0-5][0-9])$/
  return timeRegex.test(time)
}

function TimestampField({
  field,
  fieldName,
  handleChange,
  setFieldTouched,
  facilityTimezone,
}: {
  field: OpenDockField
  fieldName: string
  handleChange: (event: any) => void
  setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void
  facilityTimezone: string
}) {
  const [time, setTime] = useState<string>('')
  const [amPm, setAmPm] = useState('AM')
  const [date, setDate] = useState<string | number | null | undefined>(null)

  useEffect(() => {
    if (isTimeValid(time) && amPm && date) {
      const [hour, minutes] = time.split(':').map(Number)
      const hourIn24Format = amPm === 'PM' ? hour + 12 : hour

      const newDate = new Date(date)
      newDate.setHours(hourIn24Format, minutes, 0, 0)

      handleChange({
        target: {
          name: fieldName,
          value: newDate.toISOString(),
        },
      })
    }
  }, [time, amPm, date, handleChange, fieldName])

  return (
    <Layout.Group style={{ flexWrap: 'nowrap' }} gap="spacing-2">
      <DatePicker
        name={`${fieldName}-date`}
        value={date}
        onChange={(event: EventLike<string | null>) => {
          setDate(event.target.value)
        }}
      />
      <Layout.Stack>
        <MaskedInput
          id="time"
          name="time"
          mask={[/[0-1]/, /\d/, ':', /[0-5]/, /\d/]}
          placeholder="00:00"
          aria-required="true"
          onChange={event => setTime((event.target as HTMLInputElement).value)}
          value={time || undefined}
          render={(ref: (inputElement: HTMLInputElement) => void, props) => (
            <LegacyMirandaTextField style={{ width: '117px' }} ref={ref} {...props} />
          )}
          pipe={conformedValue => {
            if (Number(conformedValue.substring(0, 2)) > TWELVE_HOUR_LIMIT) {
              return false
            }

            return conformedValue
          }}
        />
      </Layout.Stack>
      <ToggleGroup
        type="single-strict"
        onChange={event => {
          event.stopPropagation()
          setAmPm(String(event.detail.value))
        }}
        value={AMPM_OPTIONS.find(option => option.value === amPm)?.value}
        onBlur={() => setFieldTouched(fieldName)}
        options={AMPM_OPTIONS}
      />
    </Layout.Group>
  )
}

export type FlatennedFormValues = {
  [key: `custom-form-fields-${number}`]: OpenDockWarehouseFormFieldValue | undefined
} & {
  [key: `form-fields-${number}`]: OpenDockWarehouseFormFieldValue | undefined
} & {
  refNumbers?: string | null | undefined
}

type DynamicRefNumberFieldProps = {
  field: OpenDockWarehouseFormRefNumbers
  fieldName: string
  formikProperties: FormikProps<FlatennedFormValues>
}

function LegacyRefNumberField({ field, fieldName, formikProperties }: DynamicRefNumberFieldProps) {
  const { values, setFieldValue, errors, touched, setFieldTouched } = formikProperties

  // There is no way to determine the exact type of the value
  // because the fields are dynamic and come from the backend.
  // And If we try to narrow the type here, we will need to add
  // a lot of "as type" down below
  const formValues = values as { [key: string]: any }

  const validationStatus = getValidationStatus(fieldName, errors, touched)

  return (
    <FormField
      field={field}
      fieldName={fieldName}
      validationStatus={validationStatus}
      errors={errors}
    >
      <LegacyMirandaTextField
        name={fieldName}
        type={'text'}
        value={get(formValues, fieldName)}
        onInput={event => {
          let inputValue = (event.target as HTMLInputElement).value

          const pattern = field.inputMode === 'numeric' ? /[^0-9,]/g : /[^a-zA-Z0-9,]/g

          if (!inputValue) {
            setFieldValue(fieldName, inputValue)
            return
          }

          inputValue = inputValue.replace(pattern, '')

          inputValue = inputValue.replace(/,+/g, ',')

          setFieldValue(fieldName, inputValue)

          return { ...event, target: { ...event.target, value: inputValue } }
        }}
        onBlur={() => setFieldTouched(fieldName)}
      />
    </FormField>
  )
}

function FlexibleRefNumberField({
  field,
  fieldName,
  formikProperties,
}: DynamicRefNumberFieldProps) {
  const { values, handleChange, errors, touched, setFieldTouched } = formikProperties

  // There is no way to determine the exact type of the value
  // because the fields are dynamic and come from the backend.
  // And If we try to narrow the type here, we will need to add
  // a lot of "as type" down below
  const formValues = values as { [key: string]: any }

  const validationStatus = getValidationStatus(fieldName, errors, touched)

  return (
    <FormField
      field={field}
      fieldName={fieldName}
      validationStatus={validationStatus}
      errors={errors}
    >
      <TextField
        name={fieldName}
        type="text"
        value={get(formValues, fieldName)}
        onInput={handleChange}
        onBlur={() => setFieldTouched(fieldName)}
      />
      {validationStatus === 'default' && field.helpText && (
        <Field.Hint>{field.helpText}</Field.Hint>
      )}
    </FormField>
  )
}

export function DynamicRefNumberField(props: DynamicRefNumberFieldProps) {
  const enableFlexibleReferenceNumber = useConfigContext().enableFlexibleReferenceNumber.toBoolean()

  if (enableFlexibleReferenceNumber) {
    return <FlexibleRefNumberField {...props} />
  }

  return <LegacyRefNumberField {...props} />
}

export function DynamicField({
  field,
  fieldName,
  formikProperties,
  shipmentUUID,
  facilityTimezone,
}: {
  field: OpenDockWarehouseCustomFormField | OpenDockWarehouseFormField
  fieldName: string
  formikProperties: FormikProps<FlatennedFormValues>
  shipmentUUID: string
  facilityTimezone: string
}) {
  const { values, setFieldValue, handleChange, errors, touched, setFieldTouched } = formikProperties

  // There is no way to determine the exact type of the value
  // because the fields are dynamic and come from the backend.
  // And If we try to narrow the type here, we will need to add
  // a lot of "as type" down below
  const formValues = values as { [key: string]: any }

  const validationStatus = getValidationStatus(fieldName, errors, touched)

  switch (field.type) {
    case 'str':
    case 'int':
    case 'email':
      return (
        <FormField
          field={field}
          fieldName={fieldName}
          validationStatus={validationStatus}
          errors={errors}
        >
          <TextField
            name={fieldName}
            type={TEXT_FIELD_TYPES[field.type]}
            value={get(formValues, fieldName)}
            onInput={handleChange}
            onBlur={() => setFieldTouched(fieldName)}
          />
        </FormField>
      )
    case 'phone':
      return (
        <FormField
          field={field}
          fieldName={fieldName}
          validationStatus={validationStatus}
          errors={errors}
        >
          <MaskedInput
            id={fieldName}
            name={fieldName}
            mask={[
              '(',
              /[1-9]/,
              /\d/,
              /\d/,
              ')',
              ' ',
              /\d/,
              /\d/,
              /\d/,
              '-',
              /\d/,
              /\d/,
              /\d/,
              /\d/,
            ]}
            placeholder="(555) 555-5555"
            onChange={handleChange}
            value={get(formValues, fieldName) || undefined}
            render={(ref: (inputElement: HTMLInputElement) => void, props) => (
              <LegacyMirandaTextField style={{ width: '100%' }} ref={ref} {...props} />
            )}
          />
        </FormField>
      )
    case 'bigstr':
      return (
        <FormField
          field={field}
          fieldName={fieldName}
          validationStatus={validationStatus}
          errors={errors}
        >
          <TextArea
            name={fieldName}
            value={get(formValues, fieldName)}
            onInput={handleChange}
            onBlur={() => setFieldTouched(fieldName)}
          />
        </FormField>
      )
    case 'bool':
      return (
        <FormField
          field={field}
          fieldName={fieldName}
          validationStatus={validationStatus}
          errors={errors}
        >
          <Switch
            name={fieldName}
            checked={get(formValues, fieldName)}
            size="default"
            onChange={(event: CustomEvent<SwitchChangeEventDetail>) => {
              event.stopPropagation()
              setFieldValue(fieldName, event.detail.checked)
            }}
            onBlur={() => setFieldTouched(fieldName)}
          >
            Yes
          </Switch>
        </FormField>
      )
    case 'date':
      return (
        <FormField
          field={field}
          fieldName={fieldName}
          validationStatus={validationStatus}
          errors={errors}
        >
          <DatePicker name={fieldName} value={get(formValues, fieldName)} onChange={handleChange} />
        </FormField>
      )
    case 'dropdown':
    case 'dropdownmultiselect':
    case 'combobox':
      return (
        <FormField
          field={field}
          fieldName={fieldName}
          validationStatus={validationStatus}
          errors={errors}
        >
          <Select
            name={fieldName}
            multiple={field.type === 'dropdownmultiselect'}
            options={buildOptions(field.dropdownOptions)}
            onChange={handleChange}
            onBlur={() => setFieldTouched(fieldName)}
            value={(get(formValues, fieldName) ?? []) as Selectable | Selectable[]}
          />
        </FormField>
      )
    case 'timestamp':
      return (
        <FormField
          field={field}
          fieldName={fieldName}
          validationStatus={validationStatus}
          errors={errors}
        >
          <TimestampField
            field={field}
            fieldName={fieldName}
            handleChange={handleChange}
            setFieldTouched={setFieldTouched}
            facilityTimezone={facilityTimezone}
          />
        </FormField>
      )
    case 'doc':
    case 'multidoc':
      return (
        <DocAndMultiDocField
          field={field}
          fieldName={fieldName}
          shipmentUUID={shipmentUUID}
          setFieldValue={setFieldValue}
          value={get(formValues, fieldName)}
        />
      )
    default:
      return null
  }
}
